/**
 * User — Iconimate (https://iconimate.app/r/user.json, version 52dc8a65c3c5).
 * Animation code: copyright (c) 2026 Muhammad Ammar (smammar100), MIT.
 * Glyph geometry: Phosphor Icons, copyright (c) 2023 Phosphor Icons, MIT.
 * Shared helpers moved to ./core; motion → m (strict LazyMotion). See LICENSE.md.
 */
import { forwardRef, useImperativeHandle } from 'react';
import { m } from 'motion/react';
import type { Variants } from 'motion/react';
import { RETURN_TRANSITION, useHover } from './core';
import type { AnimatedIconHandle, AnimatedIconProps } from './core';

// GLANCE — the head checks left, sweeps across to check right, and comes back
// to centre. The head is a perfect circle, so it shifts instead of rotating (a
// circle rotated about its centre is itself). 22 units of travel keeps the chin
// on the shoulder apex; the 5-unit lift is held across the sweep so it reads as
// one continuous look rather than a bounce.

/** The glyph's own two elements, untouched. */
const HEAD = { cx: 128, cy: 96, r: 64 };
const SHOULDERS = 'M32,216c19.37-33.47,54.55-56,96-56s76.63,22.53,96,56';

/** The mark is stroke-only; it is never filled. */
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 16,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const DURATION = 1.3;
const head: Variants = {
  normal: { x: 0, y: 0, transition: RETURN_TRANSITION },
  animate: {
    x: [0, -22, -22, 22, 22, 0],
    y: [0, -5, -5, -5, -5, 0],
    transition: { duration: DURATION, ease: 'easeInOut', times: [0, 0.2, 0.34, 0.6, 0.72, 1] },
  },
};

export const UserIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(function UserIcon(
  { size = 24, style, ...props },
  ref,
) {
  const { controls, reduced, start, stop, bind } = useHover();
  useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }), [start, stop]);

  if (reduced) {
    return (
      <div {...props} {...bind} style={{ display: 'inline-flex', overflow: 'hidden', ...style }}>
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 256" fill="none">
          <circle cx={HEAD.cx} cy={HEAD.cy} r={HEAD.r} {...STROKE} />
          <path d={SHOULDERS} {...STROKE} />
        </svg>
      </div>
    );
  }

  return (
    <div {...props} {...bind} style={{ display: 'inline-flex', overflow: 'hidden', ...style }}>
      <m.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 256 256"
        fill="none"
        initial="normal"
        animate={controls}
        style={{ overflow: 'visible' }}
      >
        <m.circle cx={HEAD.cx} cy={HEAD.cy} r={HEAD.r} {...STROKE} variants={head} />
        <path d={SHOULDERS} {...STROKE} />
      </m.svg>
    </div>
  );
});
